import React, { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { NUMBER_ICON_MAP } from '@/assets/icons/topTen';
import { Box } from '@/components/lib/components';

import { CasinoGame } from '../../hooks/use-casino-games';
import { useCarouselCardSize } from '../../hooks/use-desktop-carousel';
import { TOP_TEN_COUNT, getTrendingGames } from '../../utils/trending';
import { CasinoLayoutProps } from '../../utils/utils';
import { CasinoGameCardBase } from '../CasinoGameCardBase';
import { DesktopCarousel } from './DesktopCarousel';
import { TrendingCardShadow } from './TrendingCardShadow';

const VISIBLE_CARDS = 5;
const NUMERAL_SLOT = 72;
const FULL_SIZE_ITEM_WIDTH = 176;
/** The numeral tucks under the card's left edge rather than sitting beside it. */
const NUMERAL_OVERLAP = 12;
const WIDE_NUMERAL_OVERLAP = 30;

const keyExtractor = (item: CasinoGame) => item.id;

export const TrendingLayout = ({ data, layoutHeaderProps }: CasinoLayoutProps) => {
    const itemWidth = useCarouselCardSize(VISIBLE_CARDS);
    const numeralScale = Math.min(1, itemWidth / FULL_SIZE_ITEM_WIDTH);
    const cardSize = itemWidth - NUMERAL_SLOT * numeralScale;

    const trendingData = useMemo(() => getTrendingGames(data), [data]);

    const renderItem = useCallback(
        ({ item, index }: { item: CasinoGame; index: number }) => {
            const position = index + 1;
            const Numeral = NUMBER_ICON_MAP[position];
            const numeralOverlap = position === TOP_TEN_COUNT ? WIDE_NUMERAL_OVERLAP : NUMERAL_OVERLAP;

            return (
                <Box style={[styles.item, { width: itemWidth }]}>
                    {Numeral ? (
                        <Box
                            style={[
                                styles.numeral,
                                { right: cardSize - numeralOverlap, transform: [{ scale: numeralScale }] },
                            ]}
                        >
                            <Numeral />
                        </Box>
                    ) : null}
                    <TrendingCardShadow>
                        <CasinoGameCardBase
                            game={item}
                            cardHeight={cardSize}
                            cardWidth={cardSize}
                            imageHeight={cardSize}
                        />
                    </TrendingCardShadow>
                </Box>
            );
        },
        [itemWidth, cardSize, numeralScale]
    );

    return (
        <DesktopCarousel
            data={trendingData}
            itemWidth={itemWidth}
            visibleCount={VISIBLE_CARDS}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            layoutHeaderProps={layoutHeaderProps}
        />
    );
};

const styles = StyleSheet.create({
    item: {
        alignItems: 'flex-end',
    },
    numeral: {
        position: 'absolute',
        bottom: 0,
        transformOrigin: 'bottom right',
    },
});
